import {
  GROUPED_POLY_TYPE,
  GROUP_TYPE,
  TEXT_OVERLAY_LINE_HEIGHT,
  TEXT_OVERLAY_MAX_CHARACTERS,
  TEXT_OVERLAY_MAX_FONT_SCALE,
  TEXT_OVERLAY_MAX_LINES,
  TEXT_OVERLAY_DEFAULT_BACKGROUND_COLOR,
  TEXT_OVERLAY_DEFAULT_BACKGROUND_OPACITY,
  TEXT_OVERLAY_MIN_FONT_SCALE,
  TEXT_OVERLAY_PADDING_RATIO,
  TEXT_OVERLAY_SIZE_PRESETS,
  TEXT_OVERLAY_LOWER_THIRD_FROM_BOTTOM,
} from '@helpers/constants';
import { hexToRgba } from '@helpers/utils';
import {
  BoundingPolyRect,
  RedactionConfig,
  TextOverlay,
  TextOverlaySize,
  UDRsPolyAsset,
  UDRsPolyAssetGroup,
  UDRsPolyAssetStatus,
} from '@common-modules/mediaDetails/models';
import type { GroupedBoundingPoly } from '@common/web-worker/timeline-collection';

export function normalizeTextOverlayInput(value: string) {
  const normalized = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n').slice(0, TEXT_OVERLAY_MAX_LINES);
  return lines.join('\n').slice(0, TEXT_OVERLAY_MAX_CHARACTERS);
}

export function getTextOverlayLines(text: string) {
  return normalizeTextOverlayInput(text).split('\n');
}

export function isTextOverlayEmpty(textOverlay: Pick<TextOverlay, 'text'>) {
  return normalizeTextOverlayInput(textOverlay.text).trim().length === 0;
}

/** Clamp a fontScale (fraction of frame height) to the legible/sane range. */
export function clampTextOverlayFontScale(fontScale: number) {
  return Math.min(
    Math.max(fontScale, TEXT_OVERLAY_MIN_FONT_SCALE),
    TEXT_OVERLAY_MAX_FONT_SCALE
  );
}

/**
 * Target font size in reference-space px for a given fontScale. A line renders
 * at this size unless it must shrink to fit the frame width.
 */
export function getTextOverlayFontSizePx(
  fontScale: number,
  referenceHeight = 1080
) {
  return clampTextOverlayFontScale(fontScale) * referenceHeight;
}

/** Returns the preset label whose fontScale matches, or undefined ("Custom"). */
export function matchTextOverlayFontScaleToPreset(
  fontScale: number
): TextOverlaySize | undefined {
  const entries = Object.entries(TEXT_OVERLAY_SIZE_PRESETS) as [
    TextOverlaySize,
    number,
  ][];
  const match = entries.find(([, scale]) => Math.abs(scale - fontScale) < 1e-6);
  return match?.[0];
}

export function getTextOverlayReferenceHeight(
  videoHeight?: number,
  overlayContainerHeight?: number
) {
  return videoHeight ?? overlayContainerHeight ?? 1080;
}

export function getTextOverlayPaddingPx(referenceHeight: number) {
  return Math.max(4, referenceHeight * TEXT_OVERLAY_PADDING_RATIO);
}

export function getTextOverlayLineHeightPx(fontSize: number) {
  return fontSize * TEXT_OVERLAY_LINE_HEIGHT;
}

/**
 * Measures the pixel width of a single line at a given font size/weight. The
 * renderer supplies this from a CanvasRenderingContext2D; tests supply a fake
 * so the layout math stays pure and deterministic.
 */
export type TextMeasurer = (
  text: string,
  fontPx: number,
  bold: boolean
) => number;

/**
 * How the text ended up fitting the frame:
 *  - 'ok'      every line rendered at its target size
 *  - 'shrunk'  at least one line was scaled below the target size to fit the
 *              frame width (down to the legibility floor)
 *  - 'clipped' the text block is taller than the frame; height was clamped and
 *              the overflow is clipped (UI should warn)
 */
export type TextOverlayFitState = 'ok' | 'shrunk' | 'clipped';

export interface TextOverlayLayout {
  readonly lines: string[];
  /**
   * Per-line rendered font size in px (parallel to `lines`). A line is only
   * smaller than `fontPx` when it had to shrink to fit the frame width.
   */
  readonly lineFontPx: number[];
  /** Nominal (target) font size in px for the current fontScale. */
  readonly fontPx: number;
  /** Line-height in px for the nominal font (per-line height uses lineFontPx). */
  readonly lineHeightPx: number;
  readonly paddingPx: number;
  /** Tight width of the widest line incl. padding (the hugged box width). */
  readonly blockWidthPx: number;
  /** Total text block height incl. padding, clamped to maxHeightPx when clipped. */
  readonly blockHeightPx: number;
  readonly fitState: TextOverlayFitState;
}

/**
 * Resolves the rendered lines, per-line font sizes and the tight (hugged) block
 * dimensions for a text overlay. All values are in the same
 * (reference / video-native) px space as `referenceHeight`.
 *
 * The box hugs the text: width follows the widest line and height follows the
 * line count. Lines render at the target font (fontScale × referenceHeight);
 * any line too wide for `maxWidthPx` (the frame) shrinks its own font down to
 * the legibility floor. The block height is clamped to `maxHeightPx`.
 */
export function getTextOverlayLayout(
  textOverlay: TextOverlay,
  maxWidthPx: number,
  maxHeightPx: number,
  referenceHeight: number,
  measure: TextMeasurer
): TextOverlayLayout {
  const paddingPx = getTextOverlayPaddingPx(referenceHeight);
  const availWidthPx = Math.max(maxWidthPx - paddingPx * 2, 1);
  const targetFontPx = getTextOverlayFontSizePx(
    textOverlay.fontScale,
    referenceHeight
  );
  const bold = textOverlay.bold;
  const lines = getTextOverlayLines(textOverlay.text);

  let fitState: TextOverlayFitState = 'ok';

  // Legibility floor as a FRACTION of the reference height (not a fixed px), so
  // the on-screen preview (small referenceHeight) and the native-resolution
  // burn-in floor at the same relative size instead of diverging.
  const minLineFontPx = TEXT_OVERLAY_MIN_FONT_SCALE * referenceHeight;

  // Per-line font: keep the target size unless the line is wider than the frame,
  // in which case shrink just that line (floored for legibility).
  const lineFontPx = lines.map((line) => {
    const naturalWidthPx = measure(line, targetFontPx, bold);
    if (naturalWidthPx <= availWidthPx) {
      return targetFontPx;
    }
    const shrunk = Math.max(
      targetFontPx * (availWidthPx / naturalWidthPx),
      minLineFontPx
    );
    if (shrunk < targetFontPx) {
      fitState = 'shrunk';
    }
    return shrunk;
  });

  const lineHeightPx = getTextOverlayLineHeightPx(targetFontPx);
  const blockTextHeightPx = lineFontPx.reduce(
    (sum, fontPx) => sum + getTextOverlayLineHeightPx(fontPx),
    0
  );
  let blockHeightPx = blockTextHeightPx + paddingPx * 2;
  if (blockHeightPx > maxHeightPx) {
    blockHeightPx = maxHeightPx;
    fitState = 'clipped';
  }

  const textWidthPx = Math.max(
    ...lines.map((line, i) =>
      measure(line, lineFontPx[i] ?? targetFontPx, bold)
    ),
    1
  );
  const blockWidthPx = Math.min(textWidthPx + paddingPx * 2, maxWidthPx);

  return {
    lines,
    lineFontPx,
    fontPx: targetFontPx,
    lineHeightPx,
    paddingPx,
    blockWidthPx,
    blockHeightPx,
    fitState,
  };
}

export const TEXT_OVERLAY_FONT_FAMILY = 'Roboto, "Open Sans", sans-serif';

/**
 * The background shading colour to paint behind the text: an rgba() built from
 * the overlay's backgroundColor + backgroundOpacity, or 'transparent' when the
 * background is disabled.
 */
export function getTextOverlayBackgroundColor(
  textOverlay: TextOverlay
): string {
  if (!textOverlay.backgroundEnabled) {
    return 'transparent';
  }
  // Fall back to the defaults for overlays created before these fields existed.
  return hexToRgba(
    textOverlay.backgroundColor ?? TEXT_OVERLAY_DEFAULT_BACKGROUND_COLOR,
    textOverlay.backgroundOpacity ?? TEXT_OVERLAY_DEFAULT_BACKGROUND_OPACITY
  );
}

/**
 * The reusable visual STYLE of a text overlay — colour, size, bold, alignment
 * and background — i.e. everything except the text itself, its on-screen
 * position and its time range. Used to seed a new overlay from the last-used
 * (or a neighbouring) overlay so the user's styling carries forward.
 */
export type TextOverlayStyle = Pick<
  TextOverlay,
  | 'color'
  | 'fontScale'
  | 'bold'
  | 'horizontalAlign'
  | 'backgroundEnabled'
  | 'backgroundColor'
  | 'backgroundOpacity'
>;

export function getTextOverlayStyle(
  textOverlay: TextOverlay
): TextOverlayStyle {
  return {
    color: textOverlay.color,
    fontScale: textOverlay.fontScale,
    bold: textOverlay.bold,
    horizontalAlign: textOverlay.horizontalAlign,
    backgroundEnabled: textOverlay.backgroundEnabled,
    backgroundColor: textOverlay.backgroundColor,
    backgroundOpacity: textOverlay.backgroundOpacity,
  };
}

/**
 * The style of the text overlay whose time range is nearest `positionMs`, or
 * null when there are no text overlays. Used on TDO reload (no in-session
 * last-used style) to seed a new overlay from a neighbouring one. `positionMs`
 * and the series times must be in the same (offset-adjusted) timebase.
 */
export function findNearestTextOverlayStyle(
  boundingPolys: UDRsPolyAsset['boundingPolys'],
  positionMs: number
): TextOverlayStyle | null {
  let bestStyle: TextOverlayStyle | null = null;
  let bestDistance = Infinity;
  for (const group of Object.values(boundingPolys)) {
    if (!group?.textOverlay || !isTextOnlyOverlayGroup(group)) {
      continue;
    }
    let distance = Infinity;
    for (const item of group.series) {
      const gap =
        positionMs < item.startTimeMs
          ? item.startTimeMs - positionMs
          : positionMs > item.stopTimeMs
            ? positionMs - item.stopTimeMs
            : 0;
      if (gap < distance) {
        distance = gap;
      }
      if (distance === 0) {
        break;
      }
    }
    if (distance < bestDistance) {
      bestDistance = distance;
      bestStyle = getTextOverlayStyle(group.textOverlay);
    }
  }
  return bestStyle;
}

/**
 * Builds a TextMeasurer backed by a canvas 2d context. Used by both the
 * burn-in canvas and the box auto-fit computation so DOM wrap, canvas wrap and
 * height all agree on the same font metrics.
 */
export function makeCanvasTextMeasurer(
  context: CanvasRenderingContext2D
): TextMeasurer {
  return (text, fontPx, bold) => {
    context.font = `${bold ? 700 : 400} ${fontPx}px ${TEXT_OVERLAY_FONT_FAMILY}`;
    return context.measureText(text).width;
  };
}

/** Vertical font metrics for one rendered line, in px. */
export interface TextLineMetrics {
  /** Measured INK extents of this specific string. */
  readonly actualAscent: number;
  readonly actualDescent: number;
  /** The FONT's em-box extents, which is what CSS lays a line box out from. */
  readonly fontAscent: number;
  readonly fontDescent: number;
}

export type TextLineMetricsMeasurer = (
  text: string,
  fontPx: number,
  bold: boolean
) => TextLineMetrics | null;

/**
 * Builds a metrics measurer from a canvas 2d context. Returns null when the
 * browser does not report font-bounding-box metrics (older Safari), so callers
 * fall back to plain CSS layout rather than guessing an offset.
 */
export function makeCanvasLineMetricsMeasurer(
  context: CanvasRenderingContext2D
): TextLineMetricsMeasurer {
  return (text, fontPx, bold) => {
    context.font = `${bold ? 700 : 400} ${fontPx}px ${TEXT_OVERLAY_FONT_FAMILY}`;
    const m = context.measureText(text);
    if (
      m.fontBoundingBoxAscent === undefined ||
      m.fontBoundingBoxDescent === undefined
    ) {
      return null;
    }
    return {
      actualAscent: m.actualBoundingBoxAscent ?? fontPx * 0.7,
      actualDescent: m.actualBoundingBoxDescent ?? fontPx * 0.2,
      fontAscent: m.fontBoundingBoxAscent,
      fontDescent: m.fontBoundingBoxDescent,
    };
  };
}

/**
 * How far to nudge a DOM-rendered line so it sits where the canvas burn-in puts
 * it. Positive moves the line DOWN.
 *
 * The two renderers centre a line differently, which showed up as the preview
 * text jumping a few pixels the moment playback switched from the DOM path to
 * the canvas path:
 *
 *  - The canvas centres the measured INK in the line slot — a deliberate choice,
 *    because centring the em box reads high for caption text with few
 *    descenders (see drawTextOverlay).
 *  - CSS centres the FONT's em box via half-leading, and has no way to express
 *    the former.
 *
 * The canvas is the reference: it is what gets burned into the deliverable, so
 * the preview moves to match it, not the other way round. Returns 0 when the
 * line has no ink (a blank line) or metrics are unavailable.
 */
export function getTextOverlayDomBaselineShiftPx(
  metrics: TextLineMetrics | null,
  lineHeightPx: number
): number {
  if (!metrics) {
    return 0;
  }
  const ink = metrics.actualAscent + metrics.actualDescent;
  if (ink <= 0) {
    return 0;
  }
  const cssBaseline =
    (lineHeightPx - (metrics.fontAscent + metrics.fontDescent)) / 2 +
    metrics.fontAscent;
  const canvasBaseline =
    lineHeightPx / 2 + (metrics.actualAscent - metrics.actualDescent) / 2;
  return canvasBaseline - cssBaseline;
}

/**
 * Recomputes a text overlay's bounding poly so it tightly hugs the text at the
 * current settings. Both width and height are derived from the measured text;
 * the box is anchored by its alignment (so e.g. a centered, bottom-aligned
 * overlay grows symmetrically and upward), then shifted to stay in the frame.
 *
 * Coordinates are fractions of the frame; referenceWidth/Height are the frame
 * size in px (video-native) used for the pixel-space layout math.
 */
export function computeTextOverlayPoly(
  poly: BoundingPolyRect,
  textOverlay: TextOverlay,
  referenceWidth: number,
  referenceHeight: number,
  measure: TextMeasurer
): { poly: BoundingPolyRect; layout: TextOverlayLayout } {
  const layout = getTextOverlayLayout(
    textOverlay,
    referenceWidth,
    referenceHeight,
    referenceHeight,
    measure
  );

  const widthFraction = Math.min(layout.blockWidthPx / referenceWidth, 1);
  const heightFraction = Math.min(layout.blockHeightPx / referenceHeight, 1);

  const left = poly[0].x;
  const right = poly[1].x;
  const top = poly[0].y;
  const bottom = poly[3].y;

  // When a screen position is pinned, anchor the box to that frame edge/centre
  // (so it stays put as the font size changes). Otherwise the box re-hugs
  // around its current CENTRE on both axes — text alignment does NOT move the
  // box (it only controls text justification when rendering; box placement is
  // done by dragging or the Position on Screen control). This keeps a newly
  // drawn box and an existing box behaving identically when alignment changes.
  //
  // The pin inset is an equal pixel gap on both axes: the overlay's "Inset from
  // edge" fraction of the smaller frame dimension (undefined = flush).
  const marginPx =
    (textOverlay.screenInset ?? 0) * Math.min(referenceWidth, referenceHeight);
  const marginX = marginPx / referenceWidth;
  const marginY = marginPx / referenceHeight;
  const anchorX = (left + right) / 2;
  const anchorY = (top + bottom) / 2;

  let newLeft = textOverlay.screenPositionHorizontal
    ? {
        left: marginX,
        center: Math.max((1 - widthFraction) / 2, 0),
        right: Math.max(1 - widthFraction - marginX, 0),
      }[textOverlay.screenPositionHorizontal]
    : anchorX - widthFraction / 2;
  let newTop = textOverlay.screenPositionVertical
    ? {
        top: marginY,
        center: Math.max((1 - heightFraction) / 2, 0),
        // Centre the box a third up from the bottom (ignores the edge inset,
        // like centre — it's anchored to a point, not an edge).
        lowerThird:
          1 - TEXT_OVERLAY_LOWER_THIRD_FROM_BOTTOM - heightFraction / 2,
        bottom: Math.max(1 - heightFraction - marginY, 0),
      }[textOverlay.screenPositionVertical]
    : anchorY - heightFraction / 2;

  // Keep the box inside the frame.
  newLeft = Math.min(Math.max(newLeft, 0), Math.max(1 - widthFraction, 0));
  newTop = Math.min(Math.max(newTop, 0), Math.max(1 - heightFraction, 0));

  const newRight = Math.min(newLeft + widthFraction, 1);
  const newBottom = Math.min(newTop + heightFraction, 1);

  const nextPoly: BoundingPolyRect = [
    { x: newLeft, y: newTop },
    { x: newRight, y: newTop },
    { x: newRight, y: newBottom },
    { x: newLeft, y: newBottom },
  ];

  return { poly: nextPoly, layout };
}

/**
 * Whether a group is an on-screen text overlay rather than a redaction.
 *
 * Carrying a `textOverlay` IS the answer. This used to additionally require
 * `redactionConfig.fillType` to be 'outline' or absent, to separate a text
 * overlay from "a redaction box that also has text" — but nothing honoured
 * that category. `drawPolys` draws any poly with a textOverlay as text in both
 * its blur and non-blur branches, so such a group was classified as a UDR and
 * still rendered as text; and the UI has no route to create one (the action
 * menu returns textOverlayMenu, which has no custom-fill entry, and the
 * fill-badge handler sends text overlays to the text panel rather than the
 * redaction-config menu). The fillType check was a type tag wearing a
 * redaction config's clothes; dropping it makes classification agree with
 * rendering (VE-30942).
 *
 * WHAT THIS ACTUALLY ASKS is "does the TEXT own the box?" — a text overlay's
 * box is derived from its text and fontScale, where a redaction's box is
 * dictated by the object it covers. Those two questions have the same answer
 * today only because carrying text and being text-owned are currently the same
 * set.
 *
 * They separate the moment UDRs can carry arbitrary labels: such a group has a
 * `textOverlay` but its box is still authored per segment, so this predicate
 * would misclassify it — saving it under the `textOverlays` key, re-deriving
 * its geometry from the label, and dropping it from the redaction export. That
 * is the point at which the group needs a real stored discriminator (the option
 * deliberately deferred on VE-30942), and this function — plus the fail-safe in
 * selectUdrRedactions, which already keeps exporting a redaction for a group
 * that has both text and a real fill — is the seam to change.
 */
export function isTextOnlyOverlayGroup(group: {
  readonly textOverlay?: TextOverlay;
}) {
  return !!group.textOverlay;
}

/**
 * Whether a group carries a REAL redaction fill — blur, inverse blur or
 * blackfill — as opposed to the 'outline' sentinel or nothing at all.
 *
 * This is a FAIL-SAFE, not classification. `isTextOnlyOverlayGroup` above asks
 * only whether a group has text, which is correct for everything the app can
 * actually produce. But if a group ever carried both text and a real fill,
 * treating it purely as a text overlay would drop its redaction — the engine
 * would stop redacting the region, the save would strip `redactionConfig`, and
 * a reload would make that permanent. In a redaction product the cost of being
 * wrong in that direction is not symmetric, so every path that would discard a
 * redaction checks this first and keeps treating such a group as a redaction.
 *
 * Nothing can create that state today (the fill menu has been gated for text
 * overlays since the feature shipped, and creation only ever wrote 'outline'),
 * so this should always be false for a text overlay. It is cheap insurance.
 */
export function hasRealRedactionFill(group: {
  readonly redactionConfig?: RedactionConfig;
}) {
  const fillType = group.redactionConfig?.fillType;
  return !!fillType && fillType !== 'outline';
}

/**
 * The authored box for a text overlay group, resolved ONCE per group rather
 * than per segment.
 *
 * Position comes from the overlay's own settings: a screenPosition* pin wins,
 * and `customRect` holds the dragged position when no pin is set. The series
 * rects are a derived cache for the burn-in engine (see TextOverlay's header),
 * so they are only consulted as a fallback for groups the load adapter has not
 * hoisted — which should be nothing after a round-trip through
 * udrsAssetV2Adapter, but keeps a stale store from rendering an empty box.
 *
 * The size here is immaterial: computeTextOverlayPoly re-derives width and
 * height from the text and fontScale, and ignores the position entirely when a
 * pin is set.
 */
export function resolveTextOverlayRect(
  group: Pick<UDRsPolyAssetGroup, 'textOverlay' | 'series'>
): BoundingPolyRect | undefined {
  return group.textOverlay?.customRect ?? group.series[0]?.object.boundingPoly;
}

export function getTextOverlayUdrGroupIds(
  groupId: string | undefined,
  isTextOverlayPoly: boolean,
  clusterUdrGroupIds: string[]
) {
  if (isTextOverlayPoly && groupId) {
    return [groupId];
  }

  return clusterUdrGroupIds;
}

export function isUdrAssetPolyType(type: GROUPED_POLY_TYPE) {
  return type === 'udr' || type === 'text_overlay';
}

/**
 * Whether a poly is the given text-overlay target: the exact segment, or —
 * for text overlays — any segment of the same group. Segment swaps (e.g. the
 * duration "Full" collapse) replace the segment id but keep the group, so
 * text-overlay identity checks must match by group as well as by id.
 */
export function matchesTextOverlayTarget(
  poly: {
    readonly id: string;
    readonly type: GROUPED_POLY_TYPE;
    readonly groupId: string;
  },
  target: { readonly id?: string; readonly groupId?: string }
) {
  return (
    poly.id === target.id ||
    (poly.type === 'text_overlay' &&
      !!target.groupId &&
      poly.groupId === target.groupId)
  );
}

export function getGroupedBoundingPolyType(
  group: Pick<UDRsPolyAssetGroup, 'textOverlay'>
): 'text_overlay' | 'udr' {
  return isTextOnlyOverlayGroup(group) ? 'text_overlay' : 'udr';
}

export function mapUdrGroupToGroupedBoundingPolys(
  group: UDRsPolyAssetGroup,
  groupId = group.groupId
): GroupedBoundingPoly[] {
  const type = getGroupedBoundingPolyType(group);
  // A text overlay's box is group-level (see resolveTextOverlayRect); a UDR's
  // genuinely varies per segment via spray paint and optical tracking, which is
  // the whole point of its series.
  const groupRect =
    type === 'text_overlay' ? resolveTextOverlayRect(group) : undefined;

  return group.series.map((item) => ({
    id: item.id,
    subsegmentId: item.id,
    groupId,
    clusterId: group.clusterId,
    type,
    startTimeMs: item.startTimeMs,
    stopTimeMs: item.stopTimeMs,
    boundingPoly: groupRect ?? item.object.boundingPoly,
    isDeleted: false,
    isEditable: UDRsPolyAssetStatus.PENDING !== group.status,
    shapeType: group.shapeType,
    redactionCode: group.redactionCode,
    textOverlay: group.textOverlay,
    redactionConfig: group.redactionConfig,
    version: group.version,
  }));
}

export function getUdrGroupIdsForPoly(poly: {
  readonly groupId: string;
  readonly type: GROUPED_POLY_TYPE;
}) {
  return isUdrAssetPolyType(poly.type) ? [poly.groupId] : [];
}

export function getDetectionGroupsForPoly(poly: {
  readonly groupId: string;
  readonly type: GROUPED_POLY_TYPE;
}): Array<{ id: string; type: GROUP_TYPE }> {
  if (isUdrAssetPolyType(poly.type)) {
    return [];
  }

  return [{ id: poly.groupId, type: poly.type }];
}

export function findGroupedBoundingPolyById(
  polyId: string,
  sources: {
    readonly boundingPolySeries?: ReadonlyArray<GroupedBoundingPoly>;
    readonly unselectedBoundingPolySeries?: ReadonlyArray<GroupedBoundingPoly>;
    readonly lastActivePoly?: GroupedBoundingPoly | null;
    readonly udrAsset?: UDRsPolyAsset;
  }
): GroupedBoundingPoly | null {
  const fromSelected = sources.boundingPolySeries?.find(
    (poly) => poly.id === polyId
  );
  if (fromSelected) {
    return fromSelected;
  }

  const fromUnselected = sources.unselectedBoundingPolySeries?.find(
    (poly) => poly.id === polyId
  );
  if (fromUnselected) {
    return fromUnselected;
  }

  if (sources.lastActivePoly?.id === polyId) {
    return sources.lastActivePoly;
  }

  const boundingPolys = sources.udrAsset?.boundingPolys;
  if (!boundingPolys) {
    return null;
  }

  for (const [groupId, group] of Object.entries(boundingPolys)) {
    if (!group?.series.some((item) => item.id === polyId)) {
      continue;
    }

    return (
      mapUdrGroupToGroupedBoundingPolys(group, groupId).find(
        (poly) => poly.id === polyId
      ) ?? null
    );
  }

  return null;
}
